import { useState, useEffect } from 'react'

interface SuccessModalProps {
  isOpen: boolean
  onClose: () => void
  registrationData: {
    volunteerName: string
    date: string
    day: string
    className: string
    time: string
  } | null
}

export function SuccessModal({ isOpen, onClose, registrationData }: SuccessModalProps) {
  const [isAddingToCalendar, setIsAddingToCalendar] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = originalStyle
      }
    }
  }, [isOpen])

  const generateCalendarEvent = () => {
    if (!registrationData) return ''
    
    const { volunteerName, date, className } = registrationData
    
    // Convert Israeli date format (DD.MM.YYYY) to ISO date
    const [dayNum, month, year] = date.split('.')
    const eventDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(dayNum))
    
    // Set time to 07:30 (start time)
    const startTime = new Date(eventDate)
    startTime.setHours(7, 30, 0, 0)
    
    // Set end time to 08:10 (40 minutes later)
    const endTime = new Date(eventDate)
    endTime.setHours(8, 10, 0, 0)
    
    // Format for Google Calendar URL
    const formatDateForCalendar = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
    }
    
    const title = encodeURIComponent(`תורנות הורים - כיתה ${className}`)
    const description = encodeURIComponent(
      `תורנות הורים\n` +
      `כיתה: ${className}\n` +
      `מתנדב: ${volunteerName}\n` +
      `מיקום: בית ספר יסודי אילנות תל אביב\n` +
      `הערות: להגיע 5 דקות מוקדם`
    )
    const startDate = formatDateForCalendar(startTime)
    const endDate = formatDateForCalendar(endTime)
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${description}&location=%D7%91%D7%99%D7%AA%20%D7%A1%D7%A4%D7%A8%20%D7%99%D7%A1%D7%95%D7%93%D7%99%20%D7%90%D7%99%D7%9C%D7%A0%D7%95%D7%AA%20%D7%AA%D7%9C%20%D7%90%D7%91%D7%99%D7%91`
  }

  const handleAddToCalendar = () => {
    setIsAddingToCalendar(true)
    const calendarUrl = generateCalendarEvent()
    window.open(calendarUrl, '_blank')
    setTimeout(() => {
      setIsAddingToCalendar(false)
    }, 2000)
  }

  const handleClose = () => {
    setIsAddingToCalendar(false)
    onClose()
  }

  if (!isOpen || !registrationData) return null

  return (
    <div
      className="modal-overlay fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <div
        className="modal-content w-full max-w-md max-h-[92dvh] sm:max-h-[85vh] flex flex-col bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        dir="rtl"
      >
        {/* Grab Handle for Mobile */}
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-2.5 mb-1 sm:hidden flex-shrink-0" />

        {/* Scrollable Content Body */}
        <div className="overflow-y-auto flex-grow p-4 sm:p-5 space-y-3.5 overscroll-contain">
          {/* Success Header */}
          <div className="text-center pt-1">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2 shadow-xs">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 font-hebrew">
              רישום הושלם בהצלחה! 🎉
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 font-hebrew mt-0.5">
              תודה על התנדבותך לתורנות הורים
            </p>
          </div>

          {/* Registration Details - Compact Grid */}
          <div className="bg-green-50/90 border border-green-200/80 rounded-2xl p-3.5 sm:p-4 shadow-xs">
            <h3 className="text-xs font-bold text-green-950 font-hebrew mb-2 flex items-center gap-1.5">
              <span>📋</span>
              <span>פרטי התורנות שנרשמה:</span>
            </h3>
            <div className="grid grid-cols-2 gap-2 text-xs text-green-900 font-hebrew">
              <div>
                <span className="font-semibold text-green-950">מתנדב: </span>
                <span>{registrationData.volunteerName}</span>
              </div>
              <div>
                <span className="font-semibold text-green-950">כיתה: </span>
                <span className="font-bold">{registrationData.className}</span>
              </div>
              <div>
                <span className="font-semibold text-green-950">תאריך: </span>
                <span>{registrationData.date}</span>
              </div>
              <div>
                <span className="font-semibold text-green-950">יום: </span>
                <span>{registrationData.day}</span>
              </div>
              <div className="col-span-2 pt-1.5 border-t border-green-200/60 flex items-center gap-2">
                <span className="font-semibold text-green-950">שעות תורנות:</span>
                <span className="font-bold bg-green-200/70 px-2 py-0.5 rounded-md text-green-950" dir="ltr">{registrationData.time}</span>
              </div>
            </div>
          </div>

          {/* Important Notes - Compact */}
          <div className="bg-blue-50/80 border border-blue-200/70 rounded-2xl p-3">
            <h4 className="text-xs font-bold text-blue-950 font-hebrew mb-1 flex items-center gap-1">
              <span>💡</span>
              <span>הערות חשובות לתורן:</span>
            </h4>
            <ul className="text-xs text-blue-900 font-hebrew space-y-1 list-disc list-inside">
              <li>הגיעו 5 דקות מוקדם (אפוד זוהר יימסר במקום)</li>
              <li>במקרה של מחלה, אנא הודיעו מראש</li>
            </ul>
          </div>
        </div>

        {/* Sticky Action Footer - Thumb Zone */}
        <div className="p-3.5 sm:p-4 border-t border-gray-100 flex-shrink-0 bg-white flex items-center gap-2.5 pb-[max(1rem,calc(env(safe-area-inset-bottom,0px)+0.5rem))]">
          <button
            onClick={handleAddToCalendar}
            disabled={isAddingToCalendar}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-xl font-hebrew font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 min-h-[48px] touch-manipulation"
          >
            {isAddingToCalendar ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>מוסיף ליומן...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>הוסף ליומן Google</span>
              </>
            )}
          </button>

          <button
            onClick={handleClose}
            className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-hebrew font-semibold text-sm transition-colors min-h-[48px] min-w-[75px] touch-manipulation"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  )
}
